import { createPortal } from 'react-dom';
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  X,
  Clock,
  ListChecks,
  FileText,
  CheckSquare,
  Plus,
  Check,
  Circle,
  CheckCircle,
  Trash2
} from 'lucide-react';
import AgendaItem from './AgendaItem';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function formatMeetingDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatTime(time) {
  if (!time) return '';
  return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function formatElapsedTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

export default function MeetingModeView({
  meeting,
  onClose,
  onUpdate,
  onComplete,
  documents = [],
  policies = []
}) {
  // Timer state
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Meeting data state (mirrors MeetingDetailView)
  const [notes, setNotes] = useState(meeting.notes || '');
  const [agendaItems, setAgendaItems] = useState(meeting.agendaItems || []);
  const [actionItems, setActionItems] = useState(meeting.actionItems || []);
  const [newActionItem, setNewActionItem] = useState('');
  const [expandedAgendaItems, setExpandedAgendaItems] = useState(new Set());
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragTargetIndex, setDragTargetIndex] = useState(null);
  const notesDebounceRef = useRef(null);

  // Sync state when meeting changes
  useEffect(() => {
    setNotes(meeting.notes || '');
    setAgendaItems(meeting.agendaItems || []);
    setActionItems(meeting.actionItems || []);
  }, [meeting.id, meeting.notes, meeting.agendaItems, meeting.actionItems]);

  // Elapsed time timer
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Debounced notes save
  useEffect(() => {
    if (notesDebounceRef.current) {
      clearTimeout(notesDebounceRef.current);
    }
    notesDebounceRef.current = setTimeout(() => {
      if (notes !== meeting.notes) {
        onUpdate({ notes });
      }
    }, 500);

    return () => {
      if (notesDebounceRef.current) {
        clearTimeout(notesDebounceRef.current);
      }
    };
  }, [notes, meeting.notes, onUpdate]);

  // Handle agenda item update
  const handleUpdateAgendaItem = useCallback((updatedItem) => {
    const updatedItems = agendaItems.map(item =>
      item.id === updatedItem.id ? updatedItem : item
    );
    setAgendaItems(updatedItems);
    onUpdate({ agendaItems: updatedItems, isCustomized: true });
  }, [agendaItems, onUpdate]);

  // Handle agenda item delete
  const handleDeleteAgendaItem = useCallback((itemId) => {
    const updatedItems = agendaItems.filter(item => item.id !== itemId);
    setAgendaItems(updatedItems);
    onUpdate({ agendaItems: updatedItems, isCustomized: true });
  }, [agendaItems, onUpdate]);

  // Add new agenda item
  const handleAddAgendaItem = useCallback(() => {
    const newItem = {
      id: generateUUID(),
      title: 'New Item',
      description: '',
      durationMinutes: 10,
      order: agendaItems.length,
      isCompleted: false,
      notes: '',
      linkedDocumentIds: [],
      linkedPolicyIds: []
    };
    const updatedItems = [...agendaItems, newItem];
    setAgendaItems(updatedItems);
    setExpandedAgendaItems(prev => new Set([...prev, newItem.id]));
    onUpdate({ agendaItems: updatedItems, isCustomized: true });
  }, [agendaItems, onUpdate]);

  // Toggle agenda item expand/collapse
  const handleToggleExpand = useCallback((itemId) => {
    setExpandedAgendaItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((index) => {
    setDraggedIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (draggedIndex !== null && dragTargetIndex !== null && draggedIndex !== dragTargetIndex) {
      const newItems = [...agendaItems];
      const [moved] = newItems.splice(draggedIndex, 1);
      newItems.splice(dragTargetIndex, 0, moved);
      const reordered = newItems.map((item, i) => ({ ...item, order: i }));
      setAgendaItems(reordered);
      onUpdate({ agendaItems: reordered, isCustomized: true });
    }
    setDraggedIndex(null);
    setDragTargetIndex(null);
  }, [draggedIndex, dragTargetIndex, agendaItems, onUpdate]);

  const handleDragOver = useCallback((index) => {
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragTargetIndex(index);
    }
  }, [draggedIndex]);

  // Add action item
  const handleAddActionItem = useCallback(() => {
    if (!newActionItem.trim()) return;

    const newItem = {
      id: generateUUID(),
      text: newActionItem.trim(),
      assignee: '',
      dueDate: null,
      isCompleted: false
    };

    const updatedItems = [...actionItems, newItem];
    setActionItems(updatedItems);
    setNewActionItem('');
    onUpdate({ actionItems: updatedItems });
  }, [newActionItem, actionItems, onUpdate]);

  // Toggle action item completion
  const handleToggleActionItem = useCallback((itemId) => {
    const updatedItems = actionItems.map(item =>
      item.id === itemId ? { ...item, isCompleted: !item.isCompleted } : item
    );
    setActionItems(updatedItems);
    onUpdate({ actionItems: updatedItems });
  }, [actionItems, onUpdate]);

  // Delete action item
  const handleDeleteActionItem = useCallback((itemId) => {
    const updatedItems = actionItems.filter(item => item.id !== itemId);
    setActionItems(updatedItems);
    onUpdate({ actionItems: updatedItems });
  }, [actionItems, onUpdate]);

  // Handle complete
  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const isCompleted = meeting.status === 'completed';
  const completedAgendaCount = agendaItems.filter(i => i.isCompleted).length;
  const completedActionCount = actionItems.filter(i => i.isCompleted).length;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-surface-base flex flex-col">
      {/* Color accent bar */}
      <div
        className="h-1 flex-shrink-0"
        style={{ backgroundColor: meeting.color || '#C45D3A' }}
      />

      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-surface-card">
        <div className="max-w-4xl mx-auto flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold text-text-primary truncate">
              {meeting.title}
            </h1>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-sm text-text-secondary">
                {formatMeetingDate(meeting.date)}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-text-tertiary">
                <Clock size={14} />
                {formatTime(meeting.startTime)} - {formatTime(meeting.endTime)}
              </span>
            </div>
          </div>

          {/* Timer and Close */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-hover rounded-lg">
              <Clock size={14} className="text-accent" />
              <span className="text-sm font-mono text-text-secondary">
                {formatElapsedTime(elapsedSeconds)}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors"
              title="Exit Meeting Mode (ESC)"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto py-6">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          {/* Agenda Section */}
          <section className="bg-surface-card rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ListChecks size={18} className="text-accent" />
                <h2 className="text-base font-semibold text-text-primary">Agenda</h2>
                {agendaItems.length > 0 && (
                  <span className="text-xs text-text-tertiary bg-surface-hover px-2 py-0.5 rounded-full">
                    {completedAgendaCount}/{agendaItems.length}
                  </span>
                )}
              </div>
              <button
                onClick={handleAddAgendaItem}
                className="flex items-center gap-1.5 text-sm text-accent hover:text-accent-bright transition-colors"
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>

            {agendaItems.length === 0 ? (
              <div className="text-center py-8 bg-surface-elevated rounded-lg border border-dashed border-border">
                <ListChecks size={28} className="mx-auto mb-2 text-text-muted" />
                <p className="text-sm text-text-tertiary mb-3">
                  No agenda items yet
                </p>
                <button
                  onClick={handleAddAgendaItem}
                  className="text-sm text-accent hover:text-accent-bright transition-colors"
                >
                  + Add your first agenda item
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {agendaItems.map((item, index) => (
                  <AgendaItem
                    key={item.id}
                    item={item}
                    index={index}
                    onUpdate={handleUpdateAgendaItem}
                    onDelete={handleDeleteAgendaItem}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    isDragging={draggedIndex === index}
                    isDragTarget={dragTargetIndex === index}
                    documents={documents}
                    policies={policies}
                    isExpanded={expandedAgendaItems.has(item.id)}
                    onToggleExpand={() => handleToggleExpand(item.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Meeting Notes Section */}
          <section className="bg-surface-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={18} className="text-accent" />
              <h2 className="text-base font-semibold text-text-primary">Meeting Notes</h2>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Capture key discussion points, decisions, and takeaways..."
              className="w-full min-h-[150px] p-4 text-sm bg-surface-elevated border border-border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </section>

          {/* Action Items Section */}
          <section className="bg-surface-card rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckSquare size={18} className="text-accent" />
                <h2 className="text-base font-semibold text-text-primary">Action Items</h2>
                {actionItems.length > 0 && (
                  <span className="text-xs text-text-tertiary bg-surface-hover px-2 py-0.5 rounded-full">
                    {completedActionCount}/{actionItems.length}
                  </span>
                )}
              </div>
            </div>

            {/* Action items list */}
            {actionItems.length > 0 && (
              <div className="space-y-2 mb-4">
                {actionItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      item.isCompleted
                        ? 'bg-surface-hover border-border opacity-60'
                        : 'bg-surface-elevated border-border'
                    }`}
                  >
                    <button
                      onClick={() => handleToggleActionItem(item.id)}
                      className="flex-shrink-0"
                    >
                      {item.isCompleted ? (
                        <CheckCircle size={18} className="text-success" />
                      ) : (
                        <Circle size={18} className="text-text-tertiary hover:text-accent" />
                      )}
                    </button>
                    <span className={`flex-1 text-sm ${item.isCompleted ? 'line-through text-text-tertiary' : 'text-text-primary'}`}>
                      {item.text}
                    </span>
                    {item.assignee && (
                      <span className="text-xs text-text-tertiary px-2 py-0.5 bg-surface-hover rounded">
                        {item.assignee}
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteActionItem(item.id)}
                      className="p-1 text-text-muted hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add action item */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newActionItem}
                onChange={(e) => setNewActionItem(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddActionItem()}
                placeholder="Add an action item..."
                className="flex-1 text-sm px-4 py-2.5 bg-surface-elevated border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                onClick={handleAddActionItem}
                disabled={!newActionItem.trim()}
                className="px-4 py-2.5 text-sm font-medium text-white bg-accent hover:bg-accent-bright rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={18} />
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Footer - Mark Complete */}
      {!isCompleted && (
        <div className="flex-shrink-0 px-6 py-4 border-t border-border bg-surface-card">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={handleComplete}
              className="w-full py-3 text-sm font-medium text-white bg-success hover:bg-success/90 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Check size={18} />
              Mark Meeting Complete
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
